<template>
  <div class="quarterlyCompletionStatus common_group">
    <div class="title">
      <img src="@/assets/images/informationIcon.png">
      <span>季度完成情况</span>
    </div>
    <div class="completion-table-wrapper">
      <el-table
        :data="tableRows"
        border
        size="small"
        class="completion-table"
        :span-method="spanMethod"
        :header-cell-style="headerCellStyle"
        :cell-style="bodyCellStyle"
      >
        <el-table-column
          prop="category"
          label="指标监控"
          width="100"
          align="left"
          class-name="label-cell-column"
        >
          <template #default="{ row }">
            <span>{{ row.category }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="label"
          label=""
          min-width="200"
          align="left"
          class-name="label-cell-column"
        >
          <template #default="{ row }">
            <span>{{ row.label }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="权重（%）"
          width="100"
          align="left"
        >
          <template #default="{ row }">
            <span v-if="row.isWeightGroup">{{ formatNumber(row.weightValue, 2, 'weight') }}</span>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="年度" align="left">
          <el-table-column
            label="年度目标"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatNumber(getAnnualTarget(row), row.decimals) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="系统数据统计"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatSystemDisplay(getAnnualSystem(row), row.decimals, row.key) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="实际情况"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatNumber(getAnnualActual(row), row.decimals) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="完成率"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatPercent(getAnnualCompletion(row)) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column
          v-for="period in visibleQuarters"
          :key="period.key"
          :label="period.label"
          align="left"
        >
          <el-table-column
            label="季度目标"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatNumber(getQuarterTarget(row, period.key), row.decimals) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="系统数据统计"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatSystemDisplay(getQuarterSystem(row, period.key), row.decimals, row.key) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="实际情况"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <el-input-number
                v-if="isQuarterEditable(period.key) && row.editableActual"
                v-model="row.quarterActual[period.key]"
                controls-position="right"
                :min="0"
                :max="row.max"
                :controls="false"
                :precision="row.isInteger ? 0 : row.decimals"
                :disabled="isReadonly"
                :class="['input-only-number', { 'is-error': hasQuarterActualError(row, period.key) }]"
                @change="() => normalizeActual(row, period.key)"
                @blur="() => normalizeActual(row, period.key)"
              />
              <span v-else>{{ formatNumber(row.quarterActual[period.key], row.decimals) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="完成率（%）"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatPercent(getQuarterCompletion(row, period.key)) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>
<script>
import { INDICATOR_GROUPS, QUARTER_PERIODS, INDICATOR_TYPES } from '@/utils/constants';
import { roundToDecimals } from '@/utils/utils';

const COST_CATEGORY = '成本';

export default {
  name: 'QuarterlyCompletionStatus',
  props: {
    indicatorType: {
      type: String,
      required: true
    },
    // 示例：availableQuarters = ['q1', 'q2'] 时，仅展示 Q1、Q2 列
    availableQuarters: {
      type: Array,
      default: () => []
    },
    // 示例：weightConfig = { quantity: 35, activityCount: 10, coverage: 15 }
    // 解析优先级：先按行 key，再按分组 key（如 quantity），最后按分类名（如 数量）
    weightConfig: {
      type: Object,
      default: () => ({})
    },
    // 示例：targetConfig = { activityCount: { q1: 80, q2: 90, annual: 360 } }
    // 同样支持按分组 key 或分类名配置默认目标值
    targetConfig: {
      type: Object,
      default: () => ({})
    },
    // 示例：actualConfig = { quantity: { q1: 72, q2: 88 }, amount: { annual: 1200000 } }
    // 支持与 targetConfig 相同的优先级，用于回显季度/年度实际数据
    actualConfig: {
      type: Object,
      default: () => ({})
    },
    // 示例：systemConfig = { offlineEvents: { q1: 30, annual: 120 } }
    // 回显系统统计数据，仅展示不允许编辑
    systemConfig: {
      type: Object,
      default: () => ({})
    },
    isReadonly: {
      type: Boolean,
      default: true
    },
    editEchoData: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      // 保留完整季度定义，便于后续根据数据或外部配置筛选展示
      quarterPeriods: QUARTER_PERIODS,
      rows: [],
      currentQuarterKey: '',
      activeQuarters: [],
      currentIndicatorType: this.indicatorType
    };
  },
  computed: {
    tableRows () {
      if (this.currentIndicatorType === INDICATOR_TYPES.INDUSTRY) {
        return this.rows;
      }
      return this.rows.filter(row => row.category !== COST_CATEGORY);
    },
    visibleQuarters () {
      // 优先使用父组件传入的季度集合，若为空则回退至有数据的季度集合
      const source = this.activeQuarters.length ? this.activeQuarters : this.quarterPeriods.map(item => item.key);
      const set = new Set(source);
      return this.quarterPeriods.filter(item => set.has(item.key));
    }
  },
  watch: {
    indicatorType (val) {
      this.rebuildRows(val);
    },
    // 监听外部配置的可用季度变更
    availableQuarters: {
      handler (val) {
        if (Array.isArray(val) && val.length) {
          this.activeQuarters = [...val];
        } else {
          this.updateActiveQuarters();
        }
      },
      immediate: true
    },
    // 回显目标数据
    targetConfig: {
      handler () {
        this.applySourceData();
      },
      deep: true
    },
    // 回显实际数据
    actualConfig: {
      handler () {
        this.applySourceData();
      },
      deep: true
    },
    // 回显权重
    weightConfig: {
      handler () {
        this.applySourceData();
      },
      deep: true
    },
    // 回显系统统计数据
    systemConfig: {
      handler () {
        this.applySourceData();
      },
      deep: true
    }
  },
  created () {
    this.rebuildRows(this.indicatorType);
  },
  mounted () {
    this.currentQuarterKey = this.$route.query.quarter || '';
    this.applySourceData();
  },
  methods: {
    // 验证表单数据
    validateHandler () {
      // 检查当前季度实际情况是否全部填写
      const valid = this.tableRows.every(row => row.quarterActual[this.currentQuarterKey] !== undefined);
      return new Promise((resolve) => {
        if (valid) {
          resolve(true);
        } else {
          const missingActualCells = [];
          const quartersToCheck = this.visibleQuarters.length ? this.visibleQuarters : this.quarterPeriods;
          this.tableRows.forEach(row => {
            if (!row.actualErrors) {
              this.$set(row, 'actualErrors', { q1: false, q2: false, q3: false, q4: false });
            }
            quartersToCheck.forEach(period => {
              const quarterKey = period.key;
              const editable = this.isQuarterEditable(quarterKey) && row.editableActual;
              if (!editable) {
                this.$set(row.actualErrors, quarterKey, false);
                return;
              }
              const value = this.toNumber(row.quarterActual[quarterKey]);
              const isValid = Number.isFinite(value);
              this.$set(row.actualErrors, quarterKey, !isValid);
              if (!isValid) {
                missingActualCells.push(this.formatQuarterActualMissingLabel(row, period));
              }
            });
          });
          this.$message.error('请输入当前季度实际情况');
          resolve(false);
        }
      });
    },
    // 构造提交表单数据，用于提交入参
    getQuarterParamsValue () {
      const params = {};
      // 遍历季度，构造季度实际情况入参
      this.tableRows.forEach(period => {
        params[period.key] = period['quarterActual'][this.currentQuarterKey];
      });
      return params;
    },
    // 构造行数据
    createRows (indicatorType, previousMap = {}) {
      // 创建季度模板
      const quarterTemplate = () => ({ q1: undefined, q2: undefined, q3: undefined, q4: undefined });
      const rows = [];
      INDICATOR_GROUPS.forEach(group => {
        const groupRows = this.resolveGroupRows(group, indicatorType);
        groupRows.forEach((item, index) => {
          const rowKey = item.key;
          const previous = previousMap[rowKey];
          rows.push({
            key: rowKey,
            category: group.category,
            weightGroupKey: group.weightKey,
            label: item.label,
            calc: item.calc,
            decimals: item.decimals,
            isInteger: item.isInteger,
            max: item.max,
            isWeightGroup: index === 0,
            groupSpan: index === 0 ? groupRows.length : 0,
            quarterTarget: previous ? { ...previous.quarterTarget } : quarterTemplate(),
            quarterActual: previous ? { ...previous.quarterActual } : quarterTemplate(),
            quarterSystem: previous ? { ...previous.quarterSystem } : quarterTemplate(),
            originQuarterActual: previous ? { ...previous.originQuarterActual } : quarterTemplate(),
            actualErrors: previous && previous.actualErrors
              ? { ...previous.actualErrors }
              : {
                  q1: false,
                  q2: false,
                  q3: false,
                  q4: false
                },
            annualTarget: previous ? previous.annualTarget : undefined,
            annualActual: previous ? previous.annualActual : undefined,
            annualOriginActual: previous ? previous.annualOriginActual : undefined,
            annualSystem: previous ? previous.annualSystem : undefined,
            editableActual: previous ? previous.editableActual : true,
            weightValue: previous ? previous.weightValue : undefined
          });
        });
      });
      return rows;
    },
    // 获取行数据快照
    snapshotRows () {
      return this.rows.reduce((acc, row) => {
        acc[row.key] = {
          quarterTarget: { ...row.quarterTarget },
          quarterActual: { ...row.quarterActual },
          quarterSystem: { ...row.quarterSystem },
          originQuarterActual: { ...row.originQuarterActual },
          actualErrors: row.actualErrors ? { ...row.actualErrors } : undefined,
          annualTarget: row.annualTarget,
          annualActual: row.annualActual,
          annualOriginActual: row.annualOriginActual,
          annualSystem: row.annualSystem,
          editableActual: row.editableActual,
          weightValue: row.weightValue
        };
        return acc;
      }, {});
    },
    // 重建行数据
    rebuildRows (nextType) {
      const resolvedType = nextType;
      if (!resolvedType) {
        this.rows = [];
        this.currentIndicatorType = undefined;
        return;
      }
      if (resolvedType === this.currentIndicatorType && this.rows.length) {
        return;
      }
      const previousMap = this.snapshotRows();
      this.currentIndicatorType = resolvedType;
      this.rows = this.createRows(resolvedType, previousMap);
      this.applySourceData();
    },
    // 按类型过滤指标行，避免展示无关字段
    resolveGroupRows (group, indicatorType) {
      const definitions = Array.isArray(group.rows) ? group.rows : [];
      const filtered = definitions.filter(item => {
        if (!Array.isArray(item.types) || item.types.length === 0) {
          return true;
        }
        return item.types.includes(indicatorType);
      });
      const orderCache = new Map();
      definitions.forEach((item, idx) => {
        orderCache.set(item.key, idx);
      });
      return filtered.sort((a, b) => {
        const orderA = this.resolveRowOrder(a, indicatorType, orderCache.get(a.key));
        const orderB = this.resolveRowOrder(b, indicatorType, orderCache.get(b.key));
        return orderA - orderB;
      });
    },
    resolveRowOrder (definition, indicatorType, fallbackIndex = 0) {
      if (
        definition &&
        definition.typeOrder &&
        Object.prototype.hasOwnProperty.call(definition.typeOrder, indicatorType)
      ) {
        return definition.typeOrder[indicatorType];
      }
      return Number.isFinite(fallbackIndex) ? fallbackIndex : 0;
    },
    // 应用源数据
    applySourceData () {
      this.rows.forEach(row => {
        if (!row.actualErrors) {
          this.$set(row, 'actualErrors', {
            q1: false,
            q2: false,
            q3: false,
            q4: false
          });
        }
        row.weightValue = this.resolveWeight(row);
        const targetRow = this.resolveRowData(this.targetConfig, row);
        const actualRow = this.resolveRowData(this.actualConfig, row);
        const systemRow = this.resolveRowData(this.systemConfig, row);
        const originQuarterMap = row.originQuarterActual ? { ...row.originQuarterActual } : {};
        this.quarterPeriods.forEach(period => {
          const normalizedTarget = this.normalizeSourceValue(targetRow[period.key], row.decimals);
          const normalizedActual = this.normalizeSourceValue(actualRow[period.key], row.decimals);
          const systemValue = systemRow[period.key];
          this.$set(row.quarterTarget, period.key, normalizedTarget);
          this.$set(row.quarterActual, period.key, normalizedActual);
          this.$set(row.quarterSystem, period.key, systemValue);
          this.$set(row.actualErrors, period.key, false);
          if (!Object.prototype.hasOwnProperty.call(originQuarterMap, period.key)) {
            originQuarterMap[period.key] = normalizedActual;
          }
        });
        this.$set(row, 'originQuarterActual', originQuarterMap); // 记录原始季度实际值，叠加时用于扣除历史数据
        const normalizedAnnual = this.normalizeSourceValue(targetRow.annual, row.decimals);
        row.annualTarget = normalizedAnnual; // annual 字段存储年度目标
        const normalizedAnnualActual = this.normalizeSourceValue(actualRow.annual, row.decimals);
        row.annualActual = normalizedAnnualActual; // annual 字段存储年度实际
        const normalizedAnnualOrigin = this.normalizeSourceValue(actualRow.annualResult, row.decimals);
        row.annualOriginActual = Number.isFinite(normalizedAnnualOrigin) ? normalizedAnnualOrigin : normalizedAnnualActual; // annualResult 字段存储年度实际原始值
        const annualSystem = systemRow.annual;
        row.annualSystem = annualSystem;
      });
      this.updateActiveQuarters();
    },
    // 解析权重值
    resolveWeight (row) {
      const keys = [row.key, row.weightGroupKey, row.category];
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!key) {
          continue;
        }
        if (Object.prototype.hasOwnProperty.call(this.weightConfig, key)) {
          const val = this.normalizeSourceValue(this.weightConfig[key], 2);
          if (Number.isFinite(val)) {
            return val;
          }
        }
      }
      return undefined;
    },
    // 解析行数据
    resolveRowData (source, row) {
      if (!source || typeof source !== 'object') {
        return {};
      }
      return source[row.key] || source[row.weightGroupKey] || source[row.category] || {};
    },
    // 规范化源数据值
    normalizeSourceValue (value, decimals) {
      const number = this.toNumber(value);
      if (!Number.isFinite(number)) {
        return undefined;
      }
      return this.fixPrecision(number, decimals);
    },
    // 更新活跃季度
    updateActiveQuarters () {
      if (Array.isArray(this.availableQuarters) && this.availableQuarters.length) {
        this.activeQuarters = [...this.availableQuarters];
        return;
      }
      // 当父组件未显式提供季度时，依据已有目标/实际数据推断可展示的季度列
      const set = new Set();
      this.rows.forEach(row => {
        this.quarterPeriods.forEach(period => {
          const targetVal = this.toNumber(row.quarterTarget[period.key]);
          const actualVal = this.toNumber(row.quarterActual[period.key]);
          const systemVal = row.quarterSystem[period.key];
          if (Number.isFinite(targetVal) || Number.isFinite(actualVal) || this.isPresent(systemVal)) {
            set.add(period.key);
          }
        });
      });
      if (set.size === 0) {
        this.activeQuarters = this.quarterPeriods.map(item => item.key);
        return;
      }
      this.activeQuarters = Array.from(set);
    },
    // 表格合并单元格
    spanMethod ({ row, columnIndex }) {
      if (columnIndex === 0 || columnIndex === 2) {
        return {
          rowspan: row.groupSpan,
          colspan: row.groupSpan === 0 ? 0 : 1
        };
      }
      return {
        rowspan: 1,
        colspan: 1
      };
    },
    // 表格头单元格样式
    headerCellStyle () {
      return {
        background: '#f6f7f9',
        color: '#424C57',
        borderColor: '#e2e4e9'
      };
    },
    // 表格体单元格样式
    bodyCellStyle () {
      return {
        color: '#424C57',
        borderColor: '#e2e4e9',
        padding: '6.5px 8px'
      };
    },
    // 规范化季度实际值
    normalizeActual (row, periodKey) {
      const val = this.toNumber(row.quarterActual[periodKey]);
      if (!Number.isFinite(val)) {
        this.$set(row.quarterActual, periodKey, undefined);
        if (row.actualErrors) {
          this.$set(row.actualErrors, periodKey, true);
        }
        return;
      }
      const fixed = this.fixPrecision(val, row.isInteger ? 0 : row.decimals);
      this.$set(row.quarterActual, periodKey, fixed);
      if (row.actualErrors) {
        this.$set(row.actualErrors, periodKey, false);
      }
      this.$emit('actual-change', this.collectPayload());
    },
    // 规范化年度目标值
    getAnnualTarget (row) {
      if (Number.isFinite(row.annualTarget)) {
        return row.annualTarget;
      }
      return this.calcAggregate(row.quarterTarget, row.calc, row.decimals);
    },
    // 规范化年度实际值
    getAnnualActual (row) {
      this.isGetQuarterStatus();
      const currentQuarterKey = this.currentQuarterKey;
      const canEditQuarter = currentQuarterKey ? this.isQuarterEditable(currentQuarterKey) : false;
      /// 未完成季度不允许编辑，返回当前季度实际值
      if ((!currentQuarterKey || !canEditQuarter) && !this.isGetQuarterStatus()) {
        if (Number.isFinite(row.annualActual)) {
          return row.annualActual;
        }
        return this.calcAggregate(row.quarterActual, row.calc, row.decimals);
      }
      const baseAnnual = Number.isFinite(row.annualOriginActual) ? row.annualOriginActual : row.annualActual;
      if (!Number.isFinite(baseAnnual)) {
        return this.calcAggregate(row.quarterActual, row.calc, row.decimals);
      }
      const currentVal = this.toNumber(row.quarterActual[currentQuarterKey]);
      const initialVal = row.originQuarterActual ? this.toNumber(row.originQuarterActual[currentQuarterKey]) : NaN;
      const safeCurrent = Number.isFinite(currentVal) ? currentVal : 0;
      const safeInitial = Number.isFinite(initialVal) ? initialVal : 0;
      const total = baseAnnual - safeInitial + safeCurrent; // 年度实际 = 历史累计 - 当前季度旧值 + 当前季度新值
      return this.fixPrecision(total, row.decimals);
    },
    // 规范化年度系统统计值
    getAnnualSystem (row) {
      if (this.isPresent(row.annualSystem)) {
        return row.annualSystem;
      }
      return this.calcAggregate(row.quarterSystem, row.calc, row.decimals);
    },
    // 规范化年度完成率
    getAnnualCompletion (row) {
      // 成本分类在行业类型下不计算完成率
      if (this.currentIndicatorType === INDICATOR_TYPES.INDUSTRY && row.category === COST_CATEGORY) {
        return undefined;
      }
      const actual = this.toNumber(this.getAnnualActual(row));
      const target = this.toNumber(this.getAnnualTarget(row));
      if (!Number.isFinite(actual) || !Number.isFinite(target) || target === 0) {
        return undefined;
      }
      return this.fixPrecision((actual / target) * 100, 2);
    },
    // 规范化季度目标值
    getQuarterTarget (row, quarterKey) {
      return row.quarterTarget[quarterKey];
    },
    // 规范化季度系统统计值
    getQuarterSystem (row, quarterKey) {
      return row.quarterSystem[quarterKey];
    },
    // 规范化季度完成率
    getQuarterCompletion (row, quarterKey) {
      // 成本分类在行业类型下不计算完成率
      if (this.currentIndicatorType === INDICATOR_TYPES.INDUSTRY && row.category === COST_CATEGORY) {
        return undefined;
      }
      const actual = this.toNumber(row.quarterActual[quarterKey]);
      const target = this.toNumber(row.quarterTarget[quarterKey]);
      if (!Number.isFinite(actual) || !Number.isFinite(target) || target === 0) {
        return undefined;
      }
      return this.fixPrecision((actual / target) * 100, 2);
    },
    // 计算汇总值
    calcAggregate (values, calc, decimals) {
      const numbers = [];
      Object.keys(values).forEach(key => {
        const val = this.toNumber(values[key]);
        if (Number.isFinite(val)) {
          numbers.push(val);
        }
      });
      if (!numbers.length) {
        return undefined;
      }
      const total = numbers.reduce((sum, item) => sum + item, 0);
      const result = calc === 'average' ? total / numbers.length : total;
      return this.fixPrecision(result, decimals);
    },
    // 转换为数字
    toNumber (value) {
      if (value === null || value === undefined || value === '') {
        return NaN;
      }
      const number = Number(value);
      return Number.isNaN(number) ? NaN : number;
    },
    // 固定小数位
    fixPrecision (value, decimals = 0) {
      return roundToDecimals(value, decimals);
    },
    // 格式化数字显示
    formatNumber (value, decimals = 0, type = '') {
      const number = this.toNumber(value);
      if (!Number.isFinite(number)) {
        return type !== 'weight' ? '--' : '0.00';
      }
      return decimals > 0 ? number.toFixed(decimals) : number;
    },
    formatSystemDisplay (value, decimals = 0, key = '') {
      const number = this.toNumber(value);
      if (key === 'cpsql' && typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '' && !Number.isFinite(number)) {
          return trimmed;
        }
      }
      if (!Number.isFinite(number)) {
        return '--';
      }
      return decimals > 0 ? number.toFixed(decimals) : number;
    },
    // 格式化百分比显示
    formatPercent (value) {
      const number = this.toNumber(value);
      if (!Number.isFinite(number)) {
        return '--';
      }
      return `${number.toFixed(2)}%`;
    },
    // 检查季度是否可编辑
    isQuarterEditable (quarterKey) {
      if (this.isReadonly) {
        return false;
      }
      if (!this.currentQuarterKey) {
        return true;
      }
      return this.currentQuarterKey === quarterKey;
    },
    // 获取当前季度状态
    isGetQuarterStatus () {
      const status = this.editEchoData.status;
      // 0 未开始 1 待完成 2 审批中 3 已完成 4 驳回 5 召回
      const statusList = [0, 1, 2, 4, 5];
      return statusList.includes(status); // 返回 true 则表示当前季度为未完成状态
    },
    hasQuarterActualError (row, periodKey) {
      if (!row || !row.actualErrors) {
        return false;
      }
      return Boolean(row.actualErrors[periodKey]);
    },
    formatQuarterActualMissingLabel (row, period) {
      const category = typeof row.category === 'string' ? row.category : '';
      const label = typeof row.label === 'string' ? row.label : '';
      const periodLabel = period && period.label ? period.label : '';
      return [category, label, periodLabel].filter(Boolean).join('-') || periodLabel || '季度实际';
    },
    // 收集提交数据
    collectPayload () {
      // 结构示例：[{ key: 'activityCount', quarterActual: { q1: 100, q2: 120 } }]
      return this.tableRows.map(row => ({
        key: row.key,
        category: row.category,
        label: row.label,
        weightValue: row.weightValue,
        quarterTarget: { ...row.quarterTarget },
        quarterActual: { ...row.quarterActual }
      }));
    },
    isPresent (value) {
      return value !== undefined && value !== null && value !== '';
    }
  }
};
</script>
<style lang="scss" scoped>
.quarterlyCompletionStatus {
  .completion-table-wrapper {
    margin-top: 16px;
  }
  .completion-table {
    width: 100%;
    border-color: #e2e4e9;
    ::v-deep .el-table__header th {
      background: #f6f7f9;
      border-color: #e2e4e9;
      color: #424C57;
      font-weight: normal;
    }
    ::v-deep td.el-table__cell {
      border-color: #e2e4e9;
      padding: 6.5px 8px;
      color: #424C57;
    }
    ::v-deep .el-table__header tr:first-child th:first-child {
      border-right: 0;
    }
    ::v-deep tbody tr:hover > td {
      background-color: transparent;
    }
    ::v-deep .el-table__body-wrapper {
      &::-webkit-scrollbar {
        height: 8px;
        width: 8px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: rgba(144, 147, 153, 0.3);
      }
      &::-webkit-scrollbar-thumb:hover {
        background: rgba(144, 147, 153, 0.5);
      }
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background: #ffffff;
      }
    }
    ::v-deep .is-disabled > input {
      background: transparent;
      border: none;
      color: #424C57;
      cursor: default;
      font-size: 12px;
      text-align: left;
    }
    ::v-deep tbody tr .label-cell-column {
      background: #f6f7f9;
    }
    ::v-deep tbody tr:hover>td.label-cell-column {
      background: #f6f7f9;
    }
  }
  .input-only-number {
    width: 100%;
    ::v-deep .el-input {
      height: 100%;
      input {
        height: 34px;
        font-size: 12px;
        color: #424C57;
      }
    }
    &.is-error {
      ::v-deep .el-input__inner {
        border-color: #F56C6C;
      }
    }
  }
}
</style>
